import { Fragment } from 'react';

import { ChevronLeftIcon, ChevronRightIcon } from './icons';

export interface PaginationProps {
  page: number; // 0-indexed (matches TanStack Table's pageIndex)
  pageCount: number;
  onPageChange: (page: number) => void;
}

function buildPageLinks(page: number, pageCount: number): number[] {
  const pages = new Set<number>([0, pageCount - 1, page]);
  if (page - 1 > 0) {
    pages.add(page - 1);
  }
  if (page + 1 < pageCount - 1) {
    pages.add(page + 1);
  }
  return Array.from(pages).sort((a, b) => a - b);
}

const btnClass =
  'flex h-16 w-16 items-center justify-center transition-colors hover:text-button-secondary-bg disabled:cursor-not-allowed disabled:opacity-40';

export function Pagination({ page, pageCount, onPageChange }: PaginationProps) {
  const pageLinks = buildPageLinks(page, pageCount);

  return (
    <nav
      aria-label="Pagination"
      className="text-slate-80 flex items-center gap-4 text-sm"
    >
      <button
        type="button"
        aria-label="Previous page"
        onClick={() => onPageChange(page - 1)}
        disabled={page === 0}
        className={btnClass}
      >
        <ChevronLeftIcon size={16} />
      </button>

      {pageLinks.map((p, i) => {
        const prev = pageLinks[i - 1];
        const showEllipsis = prev != null && p - prev > 1;

        return (
          <Fragment key={p}>
            {showEllipsis && (
              <span
                aria-hidden="true"
                className="hover:text-slate-90 flex items-center justify-center px-4 transition-colors select-none"
              >
                &hellip;
              </span>
            )}
            {p === page ? (
              <span
                aria-current="page"
                className="text-slate-90 flex items-center justify-center px-4 font-medium underline underline-offset-2 transition-colors"
              >
                {p + 1}
              </span>
            ) : (
              <button
                aria-label={`Page ${p + 1}`}
                onClick={() => onPageChange(p)}
                onKeyDown={(e) => e.key === 'Enter' && onPageChange(p)}
                className="hover:text-slate-90 flex cursor-pointer items-center justify-center px-4 transition-colors"
              >
                {p + 1}
              </button>
            )}
          </Fragment>
        );
      })}

      <button
        type="button"
        aria-label="Next page"
        onClick={() => onPageChange(page + 1)}
        disabled={page === pageCount - 1}
        className={btnClass}
      >
        <ChevronRightIcon size={16} />
      </button>
    </nav>
  );
}
